import React from 'react';
import { cn } from '../../../lib/utils';
import SampleTab from './SampleTab';

const formatColumnMetrics = (colStat) => {
  const parts = [];
  if (colStat.min !== undefined && colStat.max !== undefined) {
    parts.push(`${colStat.min} → ${colStat.max}`);
  }
  if (colStat.avg !== undefined) {
    const avg = typeof colStat.avg === 'number'
      ? colStat.avg.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : colStat.avg;
    parts.push(`avg ${avg}`);
  }
  return parts.length ? parts.join(' · ') : '—';
};

const SECTION_TITLE =
  'px-4 pt-3 pb-2 text-[9px] font-black uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400';

const TABLE_SHELL =
  'overflow-hidden rounded-xl border border-gray-100 dark:border-gray-800';

const TH = 'px-3 py-2 text-left';
const TH_LABEL = 'text-[9px] font-black uppercase tracking-wider text-gray-400';
const TD = 'px-3 py-2';

const DetailsSection = ({ title, children, first = false }) => (
  <section className={cn(!first && 'border-t border-gray-100 dark:border-gray-800')}>
    <h4 className={SECTION_TITLE}>{title}</h4>
    <div className="px-4 pb-4">{children}</div>
  </section>
);

const DetailsTable = ({ children, minWidth = 'min-w-[520px]' }) => (
  <div className={TABLE_SHELL}>
    <div className="overflow-x-auto custom-scrollbar">
      <table className={cn('w-full text-left border-collapse', minWidth)}>
        {children}
      </table>
    </div>
  </div>
);

const DetailsTab = ({ dataset, samples, embedded = false }) => {
  if (!dataset) return null;

  const schemaMap = (dataset.schema || []).reduce((acc, col) => {
    acc[col.name] = col.type;
    return acc;
  }, {});

  const columnStats = Object.entries(dataset.statistics?.statistics || {});

  return (
    <div className={cn(!embedded && 'h-full overflow-y-auto custom-scrollbar')}>
      <DetailsSection title="Column Statistics" first>
        <DetailsTable>
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50 dark:border-gray-900 dark:bg-gray-900/20">
              {['Column', 'Type', 'Unique', 'Nulls', 'Metrics'].map((heading, index) => (
                <th key={heading} className={cn(TH, index === 3 && 'text-right')}>
                  <span className={TH_LABEL}>{heading}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-900/50">
            {columnStats.map(([colName, colStat]) => {
              const totalRowsCount = dataset.statistics?.num_rows || 0;
              const nullPct = colStat && totalRowsCount > 0
                ? (colStat.null_count / totalRowsCount * 100).toFixed(1)
                : null;
              const colType = schemaMap[colName] || '—';

              return (
                <tr key={colName} className="hover:bg-gray-50/50 dark:hover:bg-gray-900/30 transition-colors">
                  <td className={TD}>
                    <span className="text-[11px] font-bold text-gray-900 dark:text-white font-mono break-all">
                      {colName}
                    </span>
                  </td>
                  <td className={TD}>
                    <span className="text-[9px] font-black uppercase text-emerald-600 dark:text-emerald-500 bg-emerald-50 dark:bg-emerald-900/10 px-1.5 py-0.5 rounded border border-emerald-100/50 dark:border-emerald-900/30">
                      {colType}
                    </span>
                  </td>
                  <td className={TD}>
                    <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                      {colStat.distinct_count?.toLocaleString() || '—'}
                    </span>
                  </td>
                  <td className={cn(TD, 'text-right')}>
                    {nullPct !== null && parseFloat(nullPct) > 0 ? (
                      <span className="text-[9px] font-black text-amber-600 dark:text-amber-500">{nullPct}%</span>
                    ) : (
                      <span className="text-[9px] font-bold text-gray-300">0%</span>
                    )}
                  </td>
                  <td className={TD}>
                    <span className="text-[10px] font-medium text-gray-600 dark:text-gray-300 truncate max-w-[220px] block">
                      {formatColumnMetrics(colStat)}
                    </span>
                  </td>
                </tr>
              );
            })}
            {columnStats.length === 0 && (
              <tr>
                <td colSpan="5" className="px-3 py-8 text-center">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest opacity-50">
                    No column statistics yet
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </DetailsTable>
      </DetailsSection>

      <DetailsSection title="Sample Data">
        <div className={TABLE_SHELL}>
          <SampleTab samples={samples} isFullScreen embedded compact />
        </div>
      </DetailsSection>
    </div>
  );
};

export default DetailsTab;
